/**
 * Cache Cleanup Utilities
 *
 * Handles cleanup of IndexedDB cache based on security requirements:
 * 1. Clear cache on logout
 * 2. Clear cache if refresh token is expired (stale sessions)
 */

import { cache } from './index';
import { getRefreshToken } from './oauth-browser';

/**
 * Check if refresh token has expired.
 * Duplicates logic from oauth-browser.ts but extracted for clarity.
 *
 * SECURITY NOTE: We decode the JWT without signature validation, which is safe here because:
 * 1. We only read the expiration claims (exp, first_issued_at) - no authorization decisions
 * 2. The actual token is still validated by the backend API when used
 * 3. If someone manipulates the token, they'll get a 412 error from the API and be logged out
 * 4. This prevents unnecessary API calls for obviously expired tokens, reducing server load
 *
 * @param refreshToken - The JWT refresh token to check
 * @returns true if token is expired, false otherwise
 */
function isRefreshTokenExpired(refreshToken: string): boolean {
  try {
    // Decode JWT payload (part 2 of token)
    // nosemgrep: jwt-decode-without-verify
    const payload = JSON.parse(atob(refreshToken.split('.')[1]));

    const now = Date.now();

    // Check rolling expiration (most important check!)
    // This expires first (8 hours for WEB_BROWSER)
    if (payload.exp * 1000 < now) {
      console.log('[cache-cleanup] Refresh token expired (rolling window)', {
        exp: payload.exp,
        now: Math.floor(now / 1000),
        expired: true,
      });
      return true;
    }

    // Check absolute maximum lifetime (5 years - rarely a problem)
    const firstIssuedAt = payload.first_issued_at;
    const maxLifetimeMs = 5 * 365 * 24 * 60 * 60 * 1000; // 5 years
    if (firstIssuedAt && now - firstIssuedAt > maxLifetimeMs) {
      console.log('[cache-cleanup] Refresh token exceeded absolute maximum lifetime', {
        firstIssuedAt,
        now,
        age: now - firstIssuedAt,
        maxLifetime: maxLifetimeMs,
      });
      return true;
    }

    return false;
  } catch (_e) {
    console.error('[cache-cleanup] Failed to decode refresh token:', _e);
    return true; // Consider expired if we can't decode
  }
}

/**
 * Check if we should clear the cache based on refresh token status.
 * Called during app initialization to detect stale sessions.
 *
 * @returns Promise<boolean> - true if cache was cleared, false otherwise
 */
export async function checkAndClearStaleCache(): Promise<boolean> {
  try {
    console.log('[cache-cleanup] Checking for stale cache...');

    const refreshToken = await getRefreshToken();

    // No refresh token = user not logged in = should have no cache
    if (!refreshToken) {
      console.log('[cache-cleanup] No refresh token found, clearing cache as precaution');
      await clearCache();
      return true;
    }

    // Check if refresh token is expired
    if (isRefreshTokenExpired(refreshToken)) {
      console.log('[cache-cleanup] Refresh token expired, clearing stale cache');
      await clearCache();
      return true;
    }

    console.log('[cache-cleanup] Refresh token valid, keeping cache');
    return false;
  } catch (error) {
    console.error('[cache-cleanup] Error checking stale cache:', error);
    // Don't clear cache on error - might be temporary issue
    return false;
  }
}

/**
 * Clear all IndexedDB cache data.
 * Should be called on logout and when refresh token is expired.
 */
export async function clearCache(): Promise<void> {
  try {
    console.log('[cache-cleanup] Clearing IndexedDB cache...');
    await cache.clear();
    console.log('[cache-cleanup] Cache cleared successfully');
  } catch (error) {
    console.error('[cache-cleanup] Failed to clear cache:', error);
  }
}

/**
 * Get cache statistics for debugging
 */
export async function getCacheStats(): Promise<{
  keys: string[];
  count: number;
}> {
  try {
    const keys = await cache.keys();
    return {
      keys: keys || [],
      count: keys?.length || 0,
    };
  } catch (error) {
    console.error('[cache-cleanup] Failed to get cache stats:', error);
    return { keys: [], count: 0 };
  }
}
